/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2020 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */
import { contentModeCookieName } from "@insite/client-framework/Common/ContentMode";
import { getCookie } from "@insite/client-framework/Common/Cookies";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import Zone from "@insite/client-framework/Components/Zone";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { canAddToCart } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import { WidgetDefinition } from "@insite/client-framework/Types/ContentItemDefinitions";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ProductDetailsPageContext } from "@insite/content-library/Pages/ProductDetailsPage";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { hasEnoughInventory } from "@monsma/App/Handlers/Data/Products/ProductsSelectors";
import React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState, props: HasProductContext) => {
    const configurationCompleted = state.pages.productDetails.configurationCompleted;
    const variantSelectionCompleted = state.pages.productDetails.variantSelectionCompleted;

    return {
        configurationCompleted,
        variantSelectionCompleted,
        productSettings: getSettingsCollection(state).productSettings,
        canAddToCart: canAddToCart(
            state,
            props.productContext.product,
            props.productContext.productInfo,
            configurationCompleted,
            variantSelectionCompleted,
        ),
        hasEnoughInventory: hasEnoughInventory(state, props.productContext),
    };
};

type OwnProps = WidgetProps & HasProductContext & ReturnType<typeof mapStateToProps>;

export interface AddToCartButtonQtyOrderedWrapperStyles {
    wrapper?: InjectableCss;
}

export const supportOrderingStyles: AddToCartButtonQtyOrderedWrapperStyles = {
    wrapper: {
        css: css`
            &&& {
                margin: 20px 0 25px;
                padding-top: 20px;
                border-top: 1px solid ${getColor("common.ui12")};

                > div {
                    display: flex;
                    align-items: center;

                    > * {
                        width: auto;

                        &,
                        * {
                            margin: 0;
                        }

                        &:first-child {
                            margin-right: 20px;
                        }
                    }
                }

                @media print {
                    display: none;
                }
            }
        `,
    },
};

const styles = supportOrderingStyles;

const AddToCartButtonQtyOrderedWrapperWidget: React.FC<OwnProps> = ({
    productSettings,
    hasEnoughInventory,
    canAddToCart,
    id,
}) => {
    const ableToAddToCart = productSettings.canAddToCart && hasEnoughInventory && canAddToCart;
    const isCmsEditing = getCookie(contentModeCookieName) === "Editing";

    if (!ableToAddToCart && !isCmsEditing) {
        return null;
    }

    return (
        <StyledWrapper {...styles.wrapper}>
            <Zone contentId={id} zoneName="AddToCartButtonQtyOrderedWrapper" />
        </StyledWrapper>
    );
};

export const definition: WidgetDefinition = {
    displayName: "Add to Cart Button and Qty Ordered Wrapper",
    group: "Product Details",
    allowedContexts: [ProductDetailsPageContext],
    fieldDefinitions: [],
};

const widgetModule: WidgetModule = {
    component: withProductContext(connect(mapStateToProps)(AddToCartButtonQtyOrderedWrapperWidget)),
    definition,
};

export default widgetModule;
